import { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Formik, FormikConfig, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import {
  Alert,
  Checkbox,
  Empty,
  Form,
  Menu,
  message,
  Modal,
  ModalProps,
  Radio,
  Select,
  Tooltip,
} from 'antd';
import {
  CloseCircleFilled,
  ControlOutlined,
  InfoCircleOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import { groupBy, uniqBy } from 'lodash-es';

import FormField from 'components/FormField';
import HintInput from 'components/HintInput';
import { useAvailableAccount, useAvailableStore } from 'views/Assistant/hooks';
import { upperCaseRegionRegionMap } from 'services/config/region';
import { upperCasePlatformMap } from 'services/config/platform';
import { CreateBody } from 'services/api/assistant.group';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import { SmartFieldProps } from 'hooks/useSmartField';
import { useRequest, useMount } from 'ahooks';
import { enableWa, getWaPrivatePool } from 'services/api/whatsapp';
import { StoreTag } from './GroupComponent';
import { useGetRegionLabel } from 'hooks/business/useRegion';

const Title = styled.h4`
  font-size: 16px;
  font-weight: 500;
  color: var(--fett-text-primary);
  > span {
    margin-right: 5px;
    padding: 4px;
    font-size: 12px;
    color: var(--fett-text-secondary);
    background-color: rgba(214, 232, 255, 0.2);
    border-radius: 50%;
    vertical-align: baseline;
  }
`;

const Section = styled.section`
  margin: 0 0 30px 25px;
  padding: 25px 0;
  background-color: var(--fett-erp-card-bg);
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item')} {
    display: flex;
    &-label {
      flex: 0 0 170px;
    }
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('input-affix-wrapper')} {
    width: 385px;
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('select')} {
    width: 385px;
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('checkbox-group')} {
    > label {
      margin-right: 8px;
    }
    > label + label {
      margin-left: 0;
    }
  }
  [name='storeIds'] {
    .ant-select-selector {
      margin-bottom: 12px;
    }
  }
`;

const FlatFormItem = styled(Form.Item)`
  margin-bottom: 0;
`;

const Selector = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > div {
    flex: 1 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > span {
    margin-right: 20px;
    color: var(--fett-text-quaternary);
  }
`;

const SelectedStore = styled(StoreTag)`
  margin-right: 5px;
  .anticon {
    margin-left: 5px;
  }
`;

const StoreDropdownContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
  .${({ theme }) => theme.getAntdPrefixedClassName('tabs')} {
    &-nav {
      margin-bottom: 0;
    }
    &-nav-wrap {
      padding: 0 20px;
    }
    &-content-holder {
      height: 200px;
      overflow: auto;
    }
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('menu')} {
    border: none;
    &-item::after {
      display: none;
    }
  }
`;
const PopoverContent = styled.div`
  width: 200px;
  font-size: 12px;
  color: var(--fett-text-tertiary);
  background-color: var(--fett-bg-panel);
  max-height: 160px;
  overflow-y: auto;
  word-break: break-all;
  .label {
    color: var(--fett-text-secondary);
  }
`;

const Banner = styled(Alert)`
  margin-bottom: 10px;
`;

export interface GroupFormValues extends CreateBody {
  id?: Assistant.Group['id'];
}

type FV = GroupFormValues;

export type GroupFormHelpers = FormikHelpers<FV>;

type FormRenderChildren<FV> = (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface GroupFormProps extends Omit<FormikConfig<FV>, 'initialValues'> {
  children?: ReactNode | FormRenderChildren<FV>;
  initialValues: FV;
  groupList: Assistant.Group[];
  selectedAccountList: Assistant.Account[];
  refreshKey?: string | number;
  hiddenFields?: string[];
}

const defaultInitialValues: FV = {
  name: '',
  substituteGroupType: 'UNALTERNATIVE',
  receptionScope: 'ALL_BUYER',
  tradeStatusFilters: [],
  buyerTag: [],
  substituteGroupIds: [],
  storeIds: [],
  userIds: [],
  tunnelAccountIds: [],
};
// assistantGroupTunnelAccount: [],

const nameLimit = 20;

function GroupForm(props: GroupFormProps) {
  const {
    initialValues,
    children,
    groupList,
    selectedAccountList,
    refreshKey,
    hiddenFields,
    ...otherProps
  } = props;

  const isRenderProps = typeof children === 'function';

  const { t } = useTranslate();

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      const {
        name,
        // userIds,
        // storeIds,
        receptionScope,
        tradeStatusFilters,
        buyerTag,
        substituteGroupType,
        substituteGroupIds,
      } = values;

      if (!name || !name.trim()) {
        errors.name = t('validate.nameEmpty');
      } else if (name.length > nameLimit) {
        errors.name = t('validate.nameLimit', { limit: nameLimit });
      }

      if (
        receptionScope === 'TRADE_STATUS' &&
        (!tradeStatusFilters || tradeStatusFilters.length === 0)
      ) {
        errors.tradeStatusFilters = t('validate.tradeStatusEmpty');
      }

      if (receptionScope === 'BUYER_LABEL' && (!buyerTag || buyerTag.length === 0)) {
        errors.buyerTag = t('validate.buyerTagEmpty');
      }

      if (
        substituteGroupType === 'CERTAIN_GROUP' &&
        (!substituteGroupIds || substituteGroupIds.length === 0)
      ) {
        errors.substituteGroupIds = t('validate.groupEmpty');
      }

      return errors;
    },
    [t],
  );

  return (
    <Formik
      key={refreshKey}
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validate={validate}
      {...otherProps}
    >
      {(props) => {
        const form = (
          <GroupFormContent
            formProps={props}
            groupList={groupList}
            selectedAccountList={selectedAccountList}
            hiddenFields={hiddenFields}
          />
        );

        return isRenderProps ? (children as FormRenderChildren<FV>)(form, props) : form;
      }}
    </Formik>
  );
}

export interface GroupFormContentProps {
  formProps: FormikProps<FV>;
  groupList: Assistant.Group[];
  selectedAccountList: Assistant.Account[];
  hiddenFields?: string[];
}

const PlatformField = () => {
  const {
    loading: storeListLoading,
    list: storeList,
    fetchList: fetchStoreList,
  } = useAvailableStore();

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const { setFieldValue, values } = useFormikContext<FV>();
  const [selectedPlatform, setPlatform] = useState<string[]>([]);

  const { config } = useStoreNameConfig();
  const {
    data: isWaEnable,
    run: checkWaEnable,
    loading: waEnableLoading,
  } = useRequest(enableWa, { manual: true });
  const { data: privateWaDomainPool = [], run: fetchPrivateWaDomainPool } = useRequest(
    () => getWaPrivatePool(values.id),
    { manual: true },
  );
  useMount(() => {
    fetchStoreList().catch(() => {
      message.error(t('group.fetchStoreListFailed'));
    });
    checkWaEnable();
    fetchPrivateWaDomainPool();
  });

  const isAliasFirst = useCallback(
    (platform: General.Platform, region?: General.UpperCaseRegion) => {
      return isStoreAliasFirst(config, platform, region);
    },
    [config],
  );
  const { storeMap, platformRegionStoreMap } = useMemo(() => {
    const initialValue: {
      storeMap: Record<Assistant.GroupAvailableStore['storeId'], Assistant.GroupAvailableStore>;
      platformRegionStoreMap: Record<
        General.Platform,
        Partial<Record<General.Region, Assistant.GroupAvailableStore[]>>
      >;
    } = {
      storeMap: {},
      platformRegionStoreMap: {
        shopee: {},
        lazada: {},
        tokopedia: {},
        tiktok: {},
        tiktok_us: {},
        aliexpress: {},
        mercado: {},
        kwai: {},
        facebook: {},
        jolect: {},
      },
    };

    return (storeList || []).reduce((res, store) => {
      const { storeMap, platformRegionStoreMap } = res;

      const { storeId, externalType = 'SHOPEE', region: upperCaseRegion = 'ID' } = store;

      const { [upperCaseRegion]: region } = upperCaseRegionRegionMap;

      const { [externalType]: platform } = upperCasePlatformMap;

      storeMap[storeId] = store;

      const { [platform]: regionStoreMap } = platformRegionStoreMap;

      const { [region]: storeList } = regionStoreMap;

      if (storeList === undefined) {
        regionStoreMap[region] = [store];
      } else {
        storeList.push(store);
      }

      return res;
    }, initialValue);
  }, [storeList]);
  const platforms = useMemo(() => {
    const grouped = groupBy(storeList, 'externalType');
    const platforms = [];
    if (grouped?.SHOPEE?.length) {
      platforms.push('Shopee');
    }
    if (grouped?.LAZADA?.length) {
      platforms.push('Lazada');
    }
    if (grouped?.TOKOPEDIA?.length) {
      platforms.push('Tokopedia');
    }
    if (grouped?.TIKTOK?.length) {
      platforms.push('TikTok');
    }
    if (grouped?.TIKTOK_US?.length) {
      platforms.push('TikTok_US');
    }
    if (grouped?.ALIEXPRESS?.length) {
      platforms.push('AliExpress');
    }
    if (grouped?.FACEBOOK?.length) {
      platforms.push('Facebook');
    }
    return platforms;
  }, [storeList]);
  useEffect(() => {
    const next = [];
    if (!isWaEnable && !next.length && platforms.length === 1) {
      next.push(platforms[0]);
    }
    setPlatform(next);
  }, [isWaEnable, platforms]);

  useEffect(() => {
    if (!values.id) {
      return;
    }
    if (selectedPlatform.length || storeListLoading || !storeList || waEnableLoading) {
      return;
    }
    const next = [];

    if (values.storeIds?.length) {
      const selectedStores =
        storeList?.filter((x) => values.storeIds?.includes(Number(x.storeId))) || [];
      const hasShopee = !!selectedStores.filter((x) => x.externalType === 'SHOPEE').length;
      const hasLazada = !!selectedStores.filter((x) => x.externalType === 'LAZADA').length;
      const hasTokopedia = !!selectedStores.filter((x) => x.externalType === 'TOKOPEDIA').length;
      const hasTiktok = !!selectedStores.filter((x) => x.externalType === 'TIKTOK').length;
      const hasTiktokUS = !!selectedStores.filter((x) => x.externalType === 'TIKTOK_US').length;
      const hasAliExpress = selectedStores.some((x) => x.externalType === 'ALIEXPRESS');
      const hasFacebook = selectedStores.some((x) => x.externalType === 'FACEBOOK');
      if (hasShopee) {
        next.push('Shopee');
      }
      if (hasLazada) {
        next.push('Lazada');
      }
      if (hasTokopedia) {
        next.push('Tokopedia');
      }
      if (hasTiktok) {
        next.push('TikTok');
      }
      if (hasTiktokUS) {
        next.push('TikTok_US');
      }
      if (hasAliExpress) {
        next.push('Aliexpress');
      }
      if (hasFacebook) {
        next.push('Facebook');
      }
    }
    if (values.tunnelAccountIds?.length && isWaEnable) {
      next.push('WhatsApp');
    }
    setPlatform(next);
  }, [
    isWaEnable,
    selectedPlatform.length,
    storeList,
    storeListLoading,
    values.id,
    values.storeIds,
    values.tunnelAccountIds?.length,
    waEnableLoading,
  ]);

  const storeSelector = (field: SmartFieldProps<number[]>['field'], p: 'Shopee' | 'Lazada') => {
    const platform = toLowerCase(p);
    const { value = [], onChange, ...others } = field;
    return (
      <Select
        mode="multiple"
        value={value}
        onChange={onChange}
        getPopupContainer={(trigger) => trigger}
        loading={storeListLoading}
        placeholder={t('placeholder.storeIds', { platform: p })}
        optionLabelProp="label"
        tagRender={(props) => {
          const { value, label: storeLabel, closable, onClose } = props;

          const {
            [value as string]: {
              storeName = storeLabel,
              externalType = 'SHOPEE',
              region = undefined,
              alias = '',
            } = {},
          } = storeMap;
          if (toUpperCase(p) !== externalType) {
            return <></>;
          }
          const { [externalType]: platform } = upperCasePlatformMap;
          const name = isAliasFirst(platform, region) ? alias || storeName : storeName;
          const popoverNode = () => {
            return (
              <PopoverContent>
                <div className="label">{t('ViewShops.店铺名称')}</div>
                <div>{storeName}</div>
                <div className="label">{t('ViewShops.店铺别名')}</div>
                <div>{alias}</div>
              </PopoverContent>
            );
          };
          return (
            <Tooltip title={popoverNode()} visible={alias ? undefined : false} color="#ffffff">
              <SelectedStore platform={externalType}>
                {name}
                {closable && <CloseCircleFilled onClick={onClose} />}
              </SelectedStore>
            </Tooltip>
          );
        }}
        dropdownRender={() => {
          const { [platform]: regionStoreMap } = platformRegionStoreMap;

          const regionList = Object.keys(regionStoreMap);
          return (
            <StoreDropdownContent>
              <div>
                {regionList.length === 0 ? (
                  <Empty />
                ) : (
                  <Menu
                    multiple
                    mode="inline"
                    defaultOpenKeys={[regionList[0]]}
                    selectedKeys={value.map((id) => `${id}`)}
                    onSelect={({ key }) => {
                      onChange([...value, parseInt(key, 10)]);
                    }}
                    onDeselect={({ key }) => {
                      onChange(value.filter((id) => `${id}` !== `${key}`));
                    }}
                  >
                    {regionList.map((region) => {
                      const { [region]: storeList = [] } = regionStoreMap;

                      return (
                        <Menu.SubMenu key={region} title={getRegionLabel(region)}>
                          {storeList.map((store) => {
                            const { storeId, storeName, isDistributed, alias } = store;
                            const name = isAliasFirst(platform, toUpperCase(region))
                              ? alias || storeName
                              : storeName;
                            const popoverNode = () => {
                              return (
                                <PopoverContent>
                                  <div className="label">{t('ViewShops.店铺名称')}</div>
                                  <div>{storeName}</div>
                                  <div className="label">{t('ViewShops.店铺别名')}</div>
                                  <div>{alias}</div>
                                </PopoverContent>
                              );
                            };
                            return (
                              <Menu.Item key={storeId}>
                                <Tooltip
                                  title={popoverNode()}
                                  visible={alias ? undefined : false}
                                  color="#ffffff"
                                >
                                  {isDistributed ? (
                                    name
                                  ) : (
                                    <Selector>
                                      <div>{name}</div>
                                      <span>{t('group.undistributedStore')}</span>
                                    </Selector>
                                  )}
                                </Tooltip>
                              </Menu.Item>
                            );
                          })}
                        </Menu.SubMenu>
                      );
                    })}
                  </Menu>
                )}
              </div>
            </StoreDropdownContent>
          );
        }}
        {...others}
      />
    );
  };
  return (
    <FormField<FV['storeIds']> name="storeIds" label={t('field.负责平台')}>
      {({ field }) => {
        return (
          <div>
            {platforms.map((item) => {
              if (!isWaEnable && platforms.length < 2) {
                return (
                  <div>
                    {selectedPlatform.includes(item) ? (
                      <div>{storeSelector(field, item as any)}</div>
                    ) : null}
                  </div>
                );
              }
              return (
                <>
                  <div>
                    <Checkbox
                      onChange={(e) => {
                        const { checked } = e.target;

                        if (checked) {
                          setPlatform((pre) => [...pre, item]);
                        } else {
                          setPlatform((pre) => pre.filter((x) => x !== item));
                          const filterByPlatform = values.storeIds.filter((id) => {
                            const target = storeList?.find((x) => String(x.storeId) === String(id));
                            return target?.externalType !== item.toUpperCase();
                          });
                          field.onChange(filterByPlatform);
                        }
                      }}
                      checked={selectedPlatform.includes(item)}
                    >
                      {item}
                    </Checkbox>
                  </div>
                  {selectedPlatform.includes(item) ? (
                    <div>{storeSelector(field, item as any)}</div>
                  ) : null}
                </>
              );
            })}

            {isWaEnable ? (
              <>
                <div>
                  <Checkbox
                    onChange={(e) => {
                      const { checked } = e.target;
                      if (checked) {
                        setPlatform((pre) => [...pre, 'WhatsApp']);
                      } else {
                        setPlatform((pre) => pre.filter((x) => x !== 'WhatsApp'));
                        setFieldValue('tunnelAccountIds', []);
                      }
                    }}
                    checked={selectedPlatform.includes('WhatsApp')}
                  >
                    WhatsApp
                  </Checkbox>
                </div>
                {selectedPlatform.includes('WhatsApp') ? (
                  <div>
                    <Select
                      placeholder={t('placeholder.whatsapp')}
                      options={privateWaDomainPool}
                      value={values.tunnelAccountIds}
                      mode="multiple"
                      onChange={(value) => {
                        setFieldValue(
                          'tunnelAccountIds',
                          value?.map((x) => Number(x)),
                        );
                      }}
                      fieldNames={{ label: 'tunnelName', value: 'tunnelAccountId' }}
                    />
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        );
      }}
    </FormField>
  );
};

function FormContent(props: GroupFormContentProps) {
  const {
    formProps: {
      initialValues,
      values: { receptionScope, substituteGroupType, userIds },
      setFieldValue,
    },
    groupList,
    selectedAccountList,
    hiddenFields = [],
  } = props;

  const { t } = useTranslate();

  const substituteGroupOptions = useMemo(() => {
    const { id: initialValueId } = initialValues;

    return groupList.reduce<{ value: string; label: string }[]>((res, group) => {
      const { id, name } = group;

      if (id === initialValueId) {
        return res;
      }

      res.push({
        value: id,
        label: name,
      });

      return res;
    }, []);
  }, [groupList, initialValues]);

  const {
    loading: accountListLoading,
    list: accountList,
    fetchList: fetchAccountList,
  } = useAvailableAccount();

  const accountOptions = useMemo(() => {
    const list = uniqBy(
      [
        ...(accountList || []),
        ...selectedAccountList.map((account) => {
          const { id, name } = account;

          return {
            assistantId: id,
            assistantName: name,
            isDistributed: true,
          };
        }),
      ],
      'assistantId',
    );

    return list.map((account) => {
      const { assistantId, assistantName, isDistributed } = account;

      return (
        <Select.Option key={assistantId} value={parseInt(assistantId, 10)} label={assistantName}>
          <Selector>
            <div>{assistantName}</div>
            {!isDistributed && <span>{t('group.undistributedAccount')}</span>}
          </Selector>
        </Select.Option>
      );
    });
  }, [accountList, selectedAccountList, t]);

  const enableAccountCount = useMemo(() => {
    if (!accountList) {
      return userIds.length;
    }

    const enableIdList = accountList.map(({ assistantId }) => assistantId);

    return userIds.filter((id) => enableIdList.includes(`${id}`)).length;
  }, [accountList, userIds]);

  const enableAccountTips = useMemo(() => {
    if (enableAccountCount === 0) {
      return t('group.当前组无启用中的客服，已选适用店铺的买家无法分配至该客服组');
    }

    if (enableAccountCount === 1) {
      return t('group.当前客服组中仅有1位启用中的客服，已选适用店铺的买家将会全部分配给该客服处理');
    }

    return t('group.当前客服组中有多位启用中的客服，已选适用店铺的买家将会平均分配');
  }, [enableAccountCount, t]);

  const refs = useRef({
    fetchAccountList,
    t,
  });
  refs.current = {
    fetchAccountList,
    t,
  };

  useEffect(() => {
    const {
      current: { fetchAccountList, t },
    } = refs;

    fetchAccountList().catch(() => {
      message.error(t('group.fetchAccountListFailed'));
    });
  }, []);
  return (
    <Form>
      <Title>
        <SnippetsOutlined />
        {t('group.basicInfo')}
      </Title>
      <Section>
        <FormField<FV['name']> name="name" required label={t('field.name')}>
          {({ field }) => {
            return <HintInput placeholder={t('placeholder.name')} {...field} limit={nameLimit} />;
          }}
        </FormField>
        {hiddenFields.includes('userIds') ? null : (
          <FormField<FV['userIds']> name="userIds" label={t('field.userIds')}>
            {({ field }) => {
              return (
                <Select
                  mode="multiple"
                  getPopupContainer={(trigger) => trigger}
                  loading={accountListLoading}
                  placeholder={t('placeholder.userIds')}
                  optionLabelProp="label"
                  {...field}
                >
                  {accountOptions}
                </Select>
              );
            }}
          </FormField>
        )}
        {hiddenFields.includes('storeIds') ? null : <PlatformField />}
      </Section>
      <Title>
        <ControlOutlined />
        <span>
          {t('group.groupConfig')}
          <Tooltip title={t('group.客服组配置hint')}>
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      </Title>
      <Section>
        <FlatFormItem required label={t('field.receptionScope')}>
          <Radio.Group
            value={receptionScope}
            onChange={(e) => setFieldValue('receptionScope', e.target.value)}
          >
            {['ALL_BUYER', 'TRADE_STATUS', 'BUYER_LABEL'].map((key) => {
              return (
                <Radio key={key} value={key}>
                  {t(`receptionScopeType.${key}`)}
                </Radio>
              );
            })}
          </Radio.Group>
        </FlatFormItem>
        {receptionScope === 'TRADE_STATUS' && (
          <FormField<FV['tradeStatusFilters']> name="tradeStatusFilters" label=" " colon={false}>
            {({ field: { onChange, ...others } }) => {
              return (
                <Checkbox.Group
                  onChange={(value) => onChange(value as Assistant.Group['tradeStatusFilters'])}
                  {...others}
                >
                  {['NO_TRADE', 'NO_PAY', 'WAIT_SHIP', 'SHIPPING', 'RECENT_TRADED', 'REFUND'].map(
                    (key) => {
                      return (
                        <Checkbox key={key} value={key}>
                          {t(`tradeStatus.${key}`)}
                        </Checkbox>
                      );
                    },
                  )}
                </Checkbox.Group>
              );
            }}
          </FormField>
        )}
        {receptionScope === 'BUYER_LABEL' && (
          <FormField<FV['buyerTag']> name="buyerTag" label=" " colon={false}>
            {({ field: { onChange, ...others } }) => {
              return (
                <Checkbox.Group
                  onChange={(value) => onChange(value as Assistant.Group['buyerTag'])}
                  {...others}
                >
                  {['NEW', 'OLD'].map((key) => {
                    return (
                      <Checkbox key={key} value={key}>
                        {t(`buyerTag.${key}`)}
                      </Checkbox>
                    );
                  })}
                </Checkbox.Group>
              );
            }}
          </FormField>
        )}
        <FlatFormItem required label={t('field.substituteGroup')}>
          <Radio.Group
            value={substituteGroupType}
            onChange={(e) => setFieldValue('substituteGroupType', e.target.value)}
          >
            {['UNALTERNATIVE', 'ALL_GROUP', 'CERTAIN_GROUP'].map((key) => {
              return (
                <Radio key={key} value={key}>
                  {t(`substituteGroupType.${key}`)}
                </Radio>
              );
            })}
          </Radio.Group>
        </FlatFormItem>
        {substituteGroupType === 'CERTAIN_GROUP' && (
          <FormField<FV['substituteGroupIds']> name="substituteGroupIds" label=" " colon={false}>
            {({ field }) => {
              return (
                <Select
                  mode="multiple"
                  getPopupContainer={(trigger) => trigger}
                  placeholder={t('placeholder.substituteGroup')}
                  options={substituteGroupOptions}
                  {...field}
                />
              );
            }}
          </FormField>
        )}
      </Section>
      <Banner banner showIcon={false} type="warning" message={enableAccountTips} />
    </Form>
  );
}

export const GroupFormContent = memo(FormContent);

export interface GroupFormModalProps
  extends ModalProps,
    Pick<GroupFormProps, 'onSubmit' | 'groupList' | 'selectedAccountList'> {
  initialValues?: GroupFormProps['initialValues'];
  onCancel: Required<ModalProps>['onCancel'];
  hiddenFields?: string[];
}

function FormModal(props: GroupFormModalProps) {
  const {
    visible,
    initialValues = defaultInitialValues,
    groupList,
    selectedAccountList,
    onSubmit,
    onCancel,
    hiddenFields,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  return (
    <GroupForm
      refreshKey={String(visible)}
      initialValues={initialValues}
      groupList={groupList}
      selectedAccountList={selectedAccountList}
      onSubmit={onSubmit}
      hiddenFields={hiddenFields}
    >
      {(form, props) => {
        const { isValid, dirty, isSubmitting, handleSubmit, resetForm } = props;

        return (
          <Modal
            visible={visible}
            width={685}
            title={t('group.editGroup')}
            okButtonProps={{
              children: t('group.save'),
              disabled: !isValid,
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            onCancel={(e) => {
              if (dirty) {
                const { destroy } = Modal.confirm({
                  title: t('group.cancelEditGroupConfirm'),
                  onOk: (e) => {
                    onCancel(e);
                    resetForm();
                    destroy();
                  },
                });
              } else {
                onCancel(e);
              }
            }}
            {...otherProps}
          >
            {form}
          </Modal>
        );
      }}
    </GroupForm>
  );
}

export const GroupFormModal = memo(FormModal);

export default memo(GroupForm);
